/** @module csi/property/Widget */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom-class",
	"dojo/dom-geometry",
	"dojo/has",
	"dojo/promise/all",
	"dojo/query",
	"dojo/store/Memory",
	"dojo/when",
	"dijit/_WidgetsInTemplateMixin",
	"src/c8-platform/type/customView",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/action",
	"csi/action/ImageReplace",
	"csi/action/ImageWhereUsed",
	"csi/api",
	"csi/constants",
	"csi/core/ExtendedPath",
	"csi/def/Action",
	"csi/enums",
	"csi/heading/_base",
	"csi/html",
	"csi/image/_base",
	"csi/locale",
	"csi/middleware/common",
	"csi/middleware/attributes/attributeKeysService",
	"csi/middleware/customView/customViewService",
	"csi/middleware/customView/options/PropertyCustomViewOptionsStore",
	"csi/middleware/customView/options/PropertyOptionsTab",
	"csi/model/image",
	"csi/reflection",
	"csi/service/viewService",
	"csi/state",
	"csi/view/_CustomViewMixin",
	"csi/view/Widget",
	"csi/widget/_DndHTML5Mixin",
	"csi/widget/InlineActionMenu",
	"csi/widget/ImageContainer",
	"csi/xml",
	"./ColumnGenerator",
	"./ImageBrowse",
	"./Preferences",
	"dojo/text!csi/templates/property.html",

	"csi/action/ImageGenerate",
	"csi/view/ToolbarAttributes",
	"./ImageDefault",
	"./ImageLabelPlugin",
	"./ImageLeft",
	"./ImageRemove",
	"./ImageRight",
], function(
	declare,
	lang,
	domClass,
	domGeometry,
	has,
	all,
	query,
	Memory,
	when,
	_WidgetsInTemplateMixin,
	{CustomViewVersion},
	csi,
	Deferred,
	_,
	csiAction,
	ImageReplace,
	ImageWhereUsed,
	api,
	constants,
	ExtendedPath,
	Action,
	enums,
	csiHeading,
	html,
	csiImage,
	locale,
	{convertEnumToComponentItem},
	{migratePropertyKeysV791},
	{getCustomViewClassImageLabels},
	PropertyCustomViewOptionsStore,
	PropertyOptionsTab,
	{getImageContainerNumberedKeys, getImageData, getImageSizeBySize, getSizeByImageSize, isImageLabelValid},
	reflection,
	{collectSpecDependencyPaths},
	state,
	_CustomViewMixin,
	Widget,
	_DndHTML5Mixin,
	InlineActionMenu,
	ImageContainer,
	xml,
	ColumnGenerator,
	ImageBrowse,
	Preferences,
	template,
) {

	var ACTION_IMAGE = "PropertyImage";
	var ACTION_BROWSE = "BrowseImage";
	var CSS_ACTIVE_IMAGE = "active";
	var IMG_MAX_HEIGHT = 512;
	var IMG_MAX_WIDTH = 768;
	var IMG_MIN_WIDTH = 400;

	csiAction.registerControllers("Property", {
		BrowseImage: "csi.property.ImageBrowseController",
		CustomViewManage: "csi.property.PropertyCustomViewController",
		CustomViewManageButton: "csi.property.PropertyCustomViewController",
		CustomViewSaveAs: "csi.property.PropertyCustomViewController",
		PropertyImageDefault: "csi.property.ImageLabelController",
		PropertyImageLabel: "csi.property.ImageLabelController",
		PropertyImageLeft: "csi.property.ImageLeftController",
		PropertyImageRight: "csi.property.ImageRightController",
		PropertyImageRemove: "csi.property.ImageRemoveController",
		PropertyImage: "csi.property.ImageController",
		ImageGenerate: "csi.action.ImageGenerateController",
	});

	function _scrollToNode(/*DOMNode*/ imgWrapper) {
		// summary: scroll current image's thumbnail to the center
		var scrollNode = imgWrapper.parentNode;
		var scrollWidth = scrollNode.offsetWidth;
		var left = imgWrapper.offsetLeft + imgWrapper.offsetWidth / 2 - scrollWidth / 2;
		var lastImage = scrollNode.lastChild;
		var maxLeft = lastImage.offsetLeft + lastImage.offsetWidth - scrollWidth;
		var scrollLeft = Math.min(Math.max(0, left), maxLeft);

		// due to decimal precision, add extra 2 pixels so that we scroll all the way (1 may be enough, but adding 2 just to be safe)
		if (imgWrapper == lastImage) {
			scrollLeft += 2;
		}

		$(scrollNode).animate({scrollLeft: scrollLeft});
	}

	/**
	 * Return localized labels for image item
	 * @param {ImageData} data
	 * @return {string}
	 */
	function getLocalizedImageDataLabels(data) {
		const labels = data.imageLabels?.map((label) => locale.getEnumName(label)) || [];
		return labels.sort();
	}

	/**
	 * Return comma-separated string containing localized image labels
	 * @param {ImageData} data
	 * @return {string}
	 */
	function getLocalizedImageDataLabel(data) {
		return getLocalizedImageDataLabels(data).join(", ");
	}

	/**
	 * Render thumbnail labels
	 * @param {ImageData} data
	 * @param {HTMLElement} refNode
	 */
	function renderThumbnailLabels(data, refNode) {
		const labels = getLocalizedImageDataLabels(data);
		const labelCount = labels.length;
		html.create("span", {class: "csi-property-thumbnail-label", innerText: labelCount ? labels[0] : ""}, refNode);

		if (labelCount > 1) {
			html.create("span", {class: "csi-property-thumbnail-label-count", innerText: labelCount}, refNode);
			$(refNode).addClass("csi-property-label-count-active");
		}
	}

	return declare([Widget, _CustomViewMixin, _DndHTML5Mixin, _WidgetsInTemplateMixin], {
		// base widget members
		baseClass: "propertyView",
		customViewOptionsComponent: PropertyOptionsTab,
		customViewOptionsStore: PropertyCustomViewOptionsStore,
		plugins: ["fullScreen", "print", "pdf", "attributes", "image", "imageDefault", "imageLeft", "imageRight", "imageRemove", "imageGenerateAction", "imageLabel", "refresh"],
		pluginsRight: ["fullScreen", "print", "pdf"],
		prefClass: Preferences,
		splitterType: "property",
		templateString: template,

		// String: column generator class
		classColumnGen: ColumnGenerator,

		// connection points
		columnsNode: null,
		headerNode: null,
		imagesNode: null,
		imageNodeCell: null,
		imageNodeContainer: null,
		labelNode: null,
		thumbnailsNode: null,
		turntableNode: null,

		// Boolean: option to force showing of image gallery even when no image
		//bForceImages: false,

		// String: indicates key of image currently shown in view
		currentImageKey: null,

		// Boolean: indicates if image gallery should be shown
		displayPropertyImage: true,

		/** @property {boolean} hideEmptyValues - if true, do not show Headings with empty values */
		hideEmptyValues: false,

		/** @property {string[]} imagePugins - list of plugins related to the image */
		imagePlugins: ["image", "imageDefault", "imageLeft", "imageRight", "imageRemove", "imageLabel"],

		/** @property {Record<string, number[]>} layoutByGroup - specify a fixed layout of the rendered group (rare) */
		layoutByGroup: null,

		// Boolean: indicates if thumbnail Label should be shown
		propertyShowThumbnailLabel: false,

		// dojox.image.Gallery: image gallery widget to display images
		imageGallery: null,

		/** @property {Spec} imageSpec - spec for image to render */
		imageSpec: null,

		// dojo.data.ItemFileReadStore: store of images
		imageStore: null,

		/**
		 * @property {boolean} printAutoScale - property views should always be scaled to fit in the view when necessary,
		 * but that should only be on rare occasions when there is large content in a particular cell that spans
		 * all columns - such as a 2D size matrix
		 */
		printAutoScale: true,

		// Array: array of Tooltip objects
		_propertyTooltips: null,

		// Number: number of property columns (if 0, then determined automatically based on available space)
		propertyColumns: 0,

		/** @property {boolean} propertyForceImageDimensions - whether force the image get rendered using the customize size or not */
		propertyForceImageDimensions: false,

		// String: label to show in image gallery by default
		//propertyImageLabel: null,

		// Number: height and width of image gallery for print
		//propertyImageHeight: null,
		//propertyImageWidth: null,

		// Boolean: indicates if image toolbar plugins are populated
		_bImagePlugins: false,

		// Object: images map when last rendered
		//_lastImages: null,

		// Number: last non-zero width for view - use when view rendered when not visible
		_lastViewWidth: 1024,

		// Object: class to generate content for headings
		//_tableGen: null,

		constructor: function() {
			this._propertyTooltips = [];
		},

		/**
		 * Return true if property view will display image gallery
		 * @return {boolean}
		 */
		displaysPropertyImage() {
			return !!this.imageSpec && this.displayPropertyImage;
		},

		findNextHeadingNode: function(/*DOMNode*/ node, /*String*/ key, /*Boolean*/ bShift) {
			// summary: default keyboard navigation is straight DOM traversal
			return csiHeading.getNextTabStop(this.columnsNode, node, key, bShift);
		},

		/**
		 * @override
		 * View needs to be redrawn when forced to be read-only
		 */
		forceReadOnly() {
			this.inherited(arguments);
			this.redraw();
		},

		getConfiguredNumberOfColumns: function() {
			// summary: return number of columns to aim for when rendering view
			return this.defView.m_nColumns || this.propertyColumns;
		},

		/**
		 * @override
		 * Return image definition
		 */
		getImage: function() {
			return this.defView.getImage();
		},

		getPrintProperties: function() {
			// summary: return object containing properties from view necessary to recreate it for printing that are not custom view options
			return {
				currentImageKey: this.getCurrentImageKey(),
			};
		},

		getColumnsForLayout: function() {
			// summary: return number of columns in view (which includes 1 for image and 1 for each property column)
			// based on current print settings
			var n = this.getConfiguredNumberOfColumns();
			if (n) {
				if (this.displaysPropertyImage()) {
					++n;
				}
			} else if (this.isPrintView()) {
				n = this.getPrintOrientation() == api.LANDSCAPE ? 3 : 2;
			} else {
				n = 3;
			}

			return n;
		},

		/**
		 * Return ImageData for selected image
		 * @return {ImageData}
		 */
		getCurrentImageData() {
			return this.imageStore?.get(this.currentImageKey);
		},

		/**
		 * Return numbered key of selected image
		 * @return {string}
		 */
		getCurrentImageKey() {
			return this.currentImageKey;
		},

		/**
		 * Return internal names of image labels (not full enum value, just keys of image container map)
		 * @return {string[]}
		 */
		getCurrentImageLabels() {
			const item = this.getCurrentImageData();
			return item?.imageLabels?.map(xml.enumGetText) || [];
		},

		/**
		 * Return node containing images
		 * @return {C8Node}
		 */
		getImageContainer() {
			const {cacheView, imageSpec} = this;
			return imageSpec && cacheView.getPathNode(this.getViewNode(), imageSpec.heading.m_path);
		},

		/**
		 * create/return new div above actions. Can be used to add extra content
		 * @param {string} cssClass - not leading dot!!
		 * @param {DomElement} [extraElement] - only added once
		 * @return {DomElement}
		 */
		getExtraPropertyDiv: function(cssClass, extraElement) {
			let div = this.scrollNode.querySelector("." + cssClass);
			if (!div) {
				div = html.create("div", {class: cssClass}, this.actionsNode, "before");
				extraElement && div.appendChild(extraElement);
			}

			return div;
		},

		hasFixedColumns: function() {
			// summary: number of columns shown in property view is fixed if specified in view definition
			return !!this.defView.m_nColumns;
		},

		isCurrentImageDefault: function() {
			// summary: return true if currently displayed image is also the default
			const item = this.imageStore?.get(this.currentImageKey);
			return item?.isDefault || false;
		},

		/**
		 * Return true if view configured to show just one image
		 * @return {boolean}
		 */
		isFixedImageView: function() {
			return this.propertyForceImageDimensions || this.isPrintView();
		},

		isPrintView: function() {
			// summary: return true if printing or if within print view
			return state.get("print") || this.getToolbarContainerView();
		},

		/**
		 * @override
		 * Remove custom view options if not supported
		 */
		onCreateDefaultCustomView(cv) {
			const defImage = this.defView.getImage();

			if (!defImage) {
				cv.removeCustomOption("displayPropertyImage");
				cv.removeCustomOption("propertyImageHeight");
				cv.removeCustomOption("propertyImageLabel");
				cv.removeCustomOption("propertyImageWidth");
			}

			if (this.hasFixedColumns()) {
				cv.removeCustomOption("propertyColumns");
			}
		},

		onClick: function(/*Event*/ e) {
			// summary: if click on attribute label, treat as click on attribute value
			var domNode = e.target;
			var attributeNode = domNode.parentNode.nextSibling;
			if (attributeNode && domClass.contains(domNode, "csiHeadingCellLabel")) {
				var nl = csiHeading.findHeadingNodes(attributeNode);
				if (nl.length == 1) {
					var actionElement = nl[0];
					csiAction.invoke(this, actionElement, null, null, e);
					return;
				}
			}

			this.inherited(arguments);
		},

		/**
		 * @override
		 * Matrices are not currently supported by property views
		 */
		onCustomViewAttributeOptions(options) {
			options.flatAttributesFirst = true;
			options.noMatrixSupport = true;
			this.inherited(arguments);
		},

		onEnablePlugins: function() {
			if (!state.get("print")) {
				var p = this.getPlugin("image");
				if (p && !p.get("disabled")) {
					this.bForceImages = true;
					html.setVisible(this.imagesNode, true);
					this.toggleDnD(true);
					// Turn Image DnD ON/Off to disable Image Paste Event Capturing before showing Image Browser (which also has Image Paste Capturing)
				} else {
					this.toggleDnD(false);
				}
			}
		},

		/**
		 * Toggle ON/Off Drag and Drop and Paste functionality
		 * @param {boolean} active
		 */
		toggleDnD: function(bActive) {
			this.dndHTML5Toggle(bActive, this.imageNodeCell, this._onDropImage, null, false);
		},

		onViewPreferences: function() {
			// summary: reset currently displayed image key
			this.inherited(arguments);
			this.set("currentImageKey", null);
		},

		redrawNode: function(/*DOMNode*/ element) {
			// summary: for property view, refreshing node requires redrawing whole view
			var pos = html.pageContentScroll().scrollTop;

			return when(this.redraw(), function() {
				html.pageContentScroll().scrollTop = pos;
			});
		},

		/**
		 * @override
		 * Migrate from flat list of attribute keys to new grouped structure
		 */
		_migrateAndValidateCustomViewValues(version, values) {
			const {defView, imageSpec} = this;
			const {optionalAttributesPaths, supportsAttributeGroups, m_rcRoot} = defView;
			const {propertyImageLabel} = values;
			const imageOwnerClass = imageSpec?.rc;

			if (version < CustomViewVersion.V791) {
				if (supportsAttributeGroups && m_rcRoot) {
					migratePropertyKeysV791(values, m_rcRoot, optionalAttributesPaths);
				}
			}

			// validate that chosen image label is still available
			if (propertyImageLabel && (!imageOwnerClass || !isImageLabelValid(imageOwnerClass, propertyImageLabel))) {
				values.propertyImageLabel = "";
			}

			this.inherited(arguments);
		},

		/**
		 * Render headings with support for client-side expressions
		 * @return {Deferred | undefined}
		 */
		_refreshColumns() {
			const ctx = this.getContext();
			ctx.bPathsQueried = true;
			ctx.preRollup();

			return this.when(this._buildColumns(ctx))
				.then(() => ctx.postRollup());
		},

		moveImage: function(/*String*/ newKey, /*Boolean*/ bLeft) {
			// summary: reset image store and set new key, then swap thumbnail images between siblings (left or right)
			var imgWrapper = html.queryDom([html.getDataAttributeSelector(html.DATA_HEADING_VALUE_KEY, this.currentImageKey)], this.thumbnailsNode, true);
			var imgWrapper1 = bLeft ? imgWrapper.previousSibling : imgWrapper;
			var imgWrapper2 = bLeft ? imgWrapper : imgWrapper.nextSibling;

			// move thumbnail and swap keys
			this.thumbnailsNode.insertBefore(imgWrapper2, imgWrapper1);
			html.setDataAttribute(imgWrapper, html.DATA_HEADING_VALUE_KEY, newKey);
			html.setDataAttribute(bLeft ? imgWrapper1 : imgWrapper2, html.DATA_HEADING_VALUE_KEY, this.currentImageKey);
			_scrollToNode(bLeft ? imgWrapper1 : imgWrapper2);

			// update store and current key
			this.imageStore = this._getImageStore();
			this.set("currentImageKey", newKey);
		},

		/**
		 * Render main image and thumbnails
		 * @param {string} [key]
		 * @return {Deferred | null}
		 */
		refreshImages(key) {
			const bPrint = state.get("print");
			const ctx = this.getContext();
			const {imageSpec} = this;
			const defImage = imageSpec?.heading;
			let node;

			if (defImage && ctx.push(defImage.m_path)) {
				node = ctx.getNode();
			}

			if (!this.turntableNode) {
				html.setVisible(this.turntableNode, false);
				return null;
			}

			html.setVisible(this.imageNodeCell, true);

			// check if anything has changed since last rendered images; if not, then leave as is
			var value = node ? node[defImage.m_id] : {};

			if (this._lastImages && _.isEqual(this._lastImages, value)) {
				return null;
			}

			this._lastImages = value;

			// display images
			const [h, w] = this._getGalleryImageDimensions();
			var imageStore = this._getImageStore();
			var images = imageStore?.query();
			var nImages = images?.length || 0;
			var bMultiple = nImages > 1;
			var imageKeyToDisplay;
			var labels;
			var src;
			let isTurntable = false;

			if (imageStore && !nImages) {
				if (this.defView.m_imageDefault) {
					// show default image if no images
					src = csi.image(this.defView.m_imageDefault);
				} else if (this.bForceImages || !node) {
					// show image node, but no image
					src = "";
				}
			} else if (imageStore) {
				// find initial key to display
				imageKeyToDisplay = key || this.currentImageKey;

				let imageItem = imageStore.get(imageKeyToDisplay);
				if (!imageItem) {
					imageKeyToDisplay = null;

					// find key for given image label if no key specified
					if (bMultiple) {
						var urlLabel = value[this.propertyImageLabel] || value[reflection.DEFAULT_IMAGE_LABEL];

						if (xml.isValidURL(urlLabel)) {
							imageKeyToDisplay = csiImage.getNumberedKey(value, urlLabel);
							imageItem = imageStore.get(imageKeyToDisplay);
						}
					}

					if (!imageItem) {
						imageItem = images[0];
						imageKeyToDisplay = imageItem.imageKey;
					}
				}

				// finally determine image label and src
				isTurntable = imageItem.isTurntable;
				src = isTurntable ? imageItem.previewGallerySrc : imageItem.previewSrc;
				labels = getLocalizedImageDataLabel(imageItem);
			}

			// hide entire image block when printing if no image
			if (bPrint && !src) {
				html.setVisible(this.imagesNode, false);
				return null;
			}

			const bShowImage = src != null;
			const browseAction = this.getAction(ACTION_BROWSE);
			const canEdit = node && browseAction.hasAccess(ctx);

			this.set("currentImageKey", imageKeyToDisplay);

			if (this.labelNode) {
				html.clear(this.labelNode);
				html.text(this.labelNode, labels);
				html.setVisible(this.labelNode, labels || !this.isPrintView());
			}

			html.clear(this.thumbnailsNode);
			html.setPixels(this.imageNodeContainer, html.WIDTH, w);
			html.setPixels(this.turntableNode, html.MAXHEIGHT, h);
			html.setPixels(this.thumbnailsNode, html.WIDTH, w);

			if (!bPrint) {
				html.setPixels(this.imageNodeContainer, html.HEIGHT, h);
			}

			html.setVisible(this.imagesNode, bShowImage);
			html.setVisible(this.imageDropMsgNode, canEdit && !src);
			html.setVisible(this.turntableNode, !!src);
			html.setVisible(this.thumbnailsNode, bMultiple);

			// Set default action (on click) for property image
			var idAction = null;

			if (node && ctx.allowActions()) {
				if (src) {
					idAction = ACTION_IMAGE;
				} else if (canEdit) {
					idAction = ACTION_BROWSE;
				}
			}

			if (idAction) {
				html.setElementAction(this.imageNodeContainer, idAction);
				html.setElementContext(this.imageNodeContainer, node.$URL);
			}

			$(this.thumbnailsNode).toggleClass("csi-property-thumbnail-show-label", this.propertyShowThumbnailLabel);

			if (bMultiple) {
				// render thumbnails when there are multiple images
				var currentImgWrapper;
				var dfds = _.map(
					images,
					function(imageItem) {
						const {
							imageKey,
							isDefault,
							isTurntable,
							thumbnailSrc,
						} = imageItem;

						// create image wrapper
						const imgWrapper = html.create("div", {
							title: getLocalizedImageDataLabel(imageItem),
						}, this.thumbnailsNode);

						html.setDataAttribute(imgWrapper, html.DATA_HEADING_VALUE_KEY, imageKey);

						if (imageKey == imageKeyToDisplay) {
							currentImgWrapper = imgWrapper;
							domClass.add(imgWrapper, CSS_ACTIVE_IMAGE);
						}

						// create thumbnail image
						const thumbContainer = html.create("div", {class: "csi-property-thumbnail-container"}, imgWrapper);

						const params = {
							disableTurntable: true,
							imageList: thumbnailSrc,
							isTurntable,
						};
						const {deferred} = new ImageContainer(params).placeAt(thumbContainer);

						// identify default image
						if (isDefault) {
							html.create("div", {class: "csiActionIcon iconDefaultImage"}, imgWrapper);
						}

						const labelContainer = html.create("div", {class: "csi-property-thumbnail-title"}, imgWrapper);
						renderThumbnailLabels(imageItem, labelContainer);

						return deferred;
					},
					this
				);

				this.when(all(dfds), function() {
					_scrollToNode(currentImgWrapper);
				});
			}

			if (src) {
				this.turntableNode.set("imageList", src);
				this.turntableNode.set("isTurntable", isTurntable);
				return this.turntableNode.deferred;
			} else {
				return null;
			}
		},

		resize: function() {
			// summary: only resize when printing - otherwise would cause too many visual artifacts
			if (state.get("print")) {
				this.inherited(arguments);
			}
		},

		/**
		 * Render attribute specs in columns
		 * @param {Context} ctx
		 * @return {Deferred | undefined}
		 */
		_buildColumns(ctx) {
			const {attributeSpecs, columnsNode, imagesNode, layoutByGroup, _propertyTooltips} = this;
			let columns = this.getConfiguredNumberOfColumns();
			let availableWidth;

			if (!attributeSpecs) {
				return;
			}

			// if part of print view, then let width available determine number of columns if not explicitly specified;
			// otherwise use default based on if image in view
			if (ctx.bPrint || (!columns && this.getToolbarContainerView())) {
				// clear content so calculations below are not influenced by existing content
				html.destroyChildren(columnsNode);

				// determine available width in view - subtract image width from full width; use only when printing
				// or when automatically determining the number of columns in a composite view
				availableWidth = this._getViewWidth();

				if (imagesNode) {
					availableWidth -= domGeometry.getContentBox(imagesNode).w;
				}
			}

			if (!ctx.bPrint && !columns) {
				columns = this.getColumnsForLayout();

				if (this.displaysPropertyImage()) {
					--columns;
				}
			}

			return this._getColumnGenerator().generate(ctx, columnsNode, attributeSpecs, columns, availableWidth, layoutByGroup, _propertyTooltips);
		},

		/**
		 * Ensure toolbar plugins to manipulate image are valid
		 */
		_buildPlugins() {
			const {imagePlugins, imageSpec, toolbarNode, _bImagePlugins} = this;

			if (!_bImagePlugins && imageSpec && toolbarNode) {
				const {heading: {m_id, m_path, m_right}, type} = imageSpec;
				const ctx = this.getContext();
				let invalidPlugins = imagePlugins;

				this._bImagePlugins = true;

				if ((m_right !== xml.RIGHT_NONE) && ctx.push(m_path) && !ctx.isReadOnly()) {
					// test each plugin individually too based on image attribute type
					invalidPlugins = imagePlugins.filter((name) => {
						const plugin = toolbarNode.getPlugin(name);

						if (plugin && (plugin.defAction.bAllowImageRef || (type === xml.ATTR_REFMAP))) {
							// add image path to new plugins if any
							plugin.defAction.idAttr = m_id;
							plugin.defAction.m_path = m_path;

							// plugin is valid
							return false;
						}

						return true;
					});
				}

				// remove invalid plugins
				toolbarNode.removePlugins(invalidPlugins, true);
			}
		},

		/**
		 * @override
		 * Render toolbar plugins, images and headings
		 */
		_buildView() {
			this._buildPlugins();
			this._destroyPropertyTooltips();

			// use default action placement if in dialog
			if (this.bPopup) {
				html.destroyElement(this.actionsNode);
				this.actionsNode = null;
			}

			// display image gallery and then attributes; display attributes after image is displayed
			// so we know how much space is available for heading columns
			this.when(this.refreshImages())
				.then(() => this._refreshColumns())
				.then(() => this._postView());
		},

		/**
		 * Return default height for main image given current settings and window size
		 * @param {boolean} bFixed
		 * @return {number}
		 */
		_getDefaultImageHeight: function(bFixed) {
			if (bFixed && this.propertyImageHeight) {
				return this.propertyImageHeight;
			} else if (this.propertyForceImageDimensions) {
				return getSizeByImageSize(this.getDefaultImageSize()).height;
			}

			return IMG_MAX_HEIGHT;
		},

		/**
		 * Return default width for main image given current settings and window size
		 * @param {boolean} bFixed
		 * @return {number}
		*/
		_getDefaultImageWidth: function(bFixed) {
			if (bFixed && this.propertyImageWidth) {
				return this.propertyImageWidth;
			} else if (this.propertyForceImageDimensions) {
				return getSizeByImageSize(this.getDefaultImageSize()).width;
			}

			var w = Math.floor(this._getViewWidth() / this.getColumnsForLayout());

			// don't allow image to exceed maximum image size for Small image size
			w = Math.min(w, IMG_MAX_WIDTH);

			// if not in print view, which means thumbnails will be shown, then make sure img has minimum width
			if (!bFixed) {
				w = Math.max(w, IMG_MIN_WIDTH);
			}

			return w;
		},

		/**
		 * Return dimensions for image gallery
		 * @return {[h: number, w: number]}
		 */
		_getGalleryImageDimensions() {
			const bFixed = this.isFixedImageView();
			const h = Math.min(this._getDefaultImageHeight(bFixed), IMG_MAX_HEIGHT);
			const w = Math.min(this._getDefaultImageWidth(bFixed), IMG_MAX_WIDTH);
			return [h, w];
		},

		_getImageAttributeId: function(/*Number*/ height, /*Number*/ width) {
			// summary: return attribute id to use for gallery image
			return csiImage.getImageIdByContext(lang.mixin(this.getContext(), {prefImageSize: getImageSizeBySize(height, width, true)}));
		},

		_getViewWidth: function() {
			// summary: return width available for view - if in print view, then possible that width is wider than screen or print page,
			// so keep within the page limit; also, if view is rendered on non-active tab, make sure to use previous width rather than 0.

			// If there are no vertical scroll bar - reserve space for it
			var w = Math.min(domGeometry.getContentBox(this.headingsNode).w, html.getPageWidth());
			if (w) {
				this._lastViewWidth = w;
			}

			return w || this._lastViewWidth;
		},

		_getImageStore: function() {
			// summary: return Memory store of images on node
			if (has("csi-no-files") || !this.displaysPropertyImage()) {
				this.imageStore = null;
				return null;
			}

			const {cacheView, propertyImageLabel} = this;
			const [h, w] = this._getGalleryImageDimensions();
			const imageId = this._getImageAttributeId(h, w);
			const containerNode = this.getImageContainer();
			const containerAttributeId = this.imageSpec.heading.m_id;
			const value = containerNode?.[containerAttributeId];
			let items;

			if (value && this.displaysPropertyImage()) {
				if (_.isObject(value)) {
					if (this.isFixedImageView()) {
						// view only displays single labeled image
						const imageUrl = value[propertyImageLabel || reflection.DEFAULT_IMAGE_LABEL];
						const imageNode = cacheView.get(imageUrl);
						if (imageNode) {
							const imageKey = csiImage.getNumberedKey(value, imageUrl);
							const rc = reflection.getNodeClass(containerNode);
							const rcEnum = rc.imageLabelEnum;
							const imageLabel = propertyImageLabel && rcEnum?.getValue(propertyImageLabel);
							items = [getImageData(imageNode, containerNode, containerAttributeId, imageKey, imageId, imageLabel)];
						}
					} else {
						// view displays all images
						items = getImageContainerNumberedKeys(value).map((imageKey) => {
							return getImageData(cacheView.get(value[imageKey]), containerNode, containerAttributeId, imageKey, imageId);
						});
					}
				} else {
					// return image data for single image referenced by image container (no image labels)
					const imageNode = cacheView.get(value);
					if (imageNode) {
						items = [getImageData(imageNode, containerNode, containerAttributeId, null, imageId)];
					}
				}
			}

			// create image store
			this.imageStore = new Memory({
				data: items || [],
				idProperty: "imageKey",
			});

			return this.imageStore;
		},

		_getColumnGenerator: function() {
			// summary: return instance of table generator
			if (!this._tableGen) {
				var ctr = csi.syncRequire(this.classColumnGen);

				this._tableGen = new ctr({
					bValidValues: this.hideEmptyValues,
					parentClass: this.defView.m_class,
				});
			}

			return this._tableGen;
		},

		_onClickThumbnail: function(/*Event*/ e) {
			// summary: change main image to clicked thumbnail, refresh toolbar when image changes so that appropriate actions are enabled/disabled
			var imgWrapper = html.getNodeWithDataAttribute(e.target, html.DATA_HEADING_VALUE_KEY, this.thumbnailsNode);
			if (imgWrapper) {
				const imageKey = html.getDataAttribute(imgWrapper, html.DATA_HEADING_VALUE_KEY);
				const imageItem = this.imageStore.get(imageKey);
				const {
					isTurntable,
					previewGallerySrc,
					previewSrc,
				} = imageItem;

				// replace main image and label
				if (this.labelNode) {
					const label = getLocalizedImageDataLabel(imageItem);
					html.clear(this.labelNode);
					html.text(this.labelNode, label || "");
				}

				this.turntableNode.set("imageList", isTurntable ? previewGallerySrc : [previewSrc]);
				this.turntableNode.set("isTurntable", isTurntable);

				// update styling and scroll selected thumbnail to center
				query("." + CSS_ACTIVE_IMAGE, this.thumbnailsNode).removeClass(CSS_ACTIVE_IMAGE);
				domClass.add(imgWrapper, CSS_ACTIVE_IMAGE);
				_scrollToNode(imgWrapper);

				this.set("currentImageKey", imageKey);

				// update toolbar based on newly selected image
				if (this.toolbarNode) {
					this.toolbarNode.enablePlugins();
				}
			}
		},

		_onDropImage: function(files) {
			const node = this.getImageContainer();
			const params = {
				m_urlNode: node.$URL,
			};
			const action = lang.mixin(new Action(), {
				m_id: "ImageDrop",
				classController: "csi/property/ImageDropController",
			});

			this._files = files;

			csiAction.invoke(this, this.domNode, action, params);
		},

		_onResize: function() {
			// summary: refresh image and column layout - when printing, no need to refresh image as its size should be constant
			// now that we do not switch the paper size after view is rendered, and the image size does not depend on scaling.
			// Another consideration is that when printing, this function cannot be asynchronous as the print calculations depend
			// on this being done in sync
			return this.when(state.get("print") ? null : this.refreshImages())
				.then(() => this._refreshColumns());
		},

		/**
		 * @override
		 * Add dependency paths for configured specs
		 */
		_onRootDependencyPaths(paths) {
			const {attributeSpecs, imageSpec} = this;

			collectSpecDependencyPaths(attributeSpecs, paths);

			if (imageSpec) {
				// do not use getHeadingDependencyPaths as we need to query for all images, not just the default one
				paths.push(imageSpec.getQueryPath(true));
			}
		},

		_refreshWidget: function() {
			// summary: reset current image being viewed to default when view is refreshed
			this._lastImages = null;
			this.set("currentImageKey", null);
			this.inherited(arguments);
		},

		/**
		 * Create spec for image before loading view
		 */
		_resolveImage() {
			const {defView, imagesNode, inlineMenuNode, specBuilder} = this;
			const ctx = this.getContext();
			const imageDef = defView.getImage();
			const spec = imageDef ? specBuilder.getSpecForHeading(imageDef) : null;

			this.imageSpec = spec;

			if (spec) {
				// determine image label store
				const rcEnum = spec.rc?.imageLabelEnum;

				if (rcEnum?.activeValues.length) {
					this.imageLabelStore = enums.getStore(rcEnum.getId(), null, null, true);
				}
			} else {
				html.orphan(imagesNode);
				this.imagesNode = null;
			}

			// remove menu if actions not allowed
			if (!ctx.allowActions()) {
				html.orphan(inlineMenuNode);
				this.inlineMenuNode = null;
			}
		},

		_resolveView: function() {
			// summary: resolve view
			this.defView._addViewAction(new ImageBrowse());
			this.defView._addViewAction(ACTION_IMAGE, {bKeepViewEnabled: true});
			this.inherited(arguments);
		},

		_setPropertyColumnsAttr: function(/*Any*/ value) {
			// summary: ensure that it is being set as number (previously was stored as string)
			this._set("propertyColumns", Number(value));
		},

		/**
		 * @override
		 * Determine if property view has image to render
		 */
		_startup() {
			this._resolveImage();
			this.inherited(arguments);
		},

		_onClickImageAction: function(a) {
			csiAction.invoke(null, null, a, {widget: this});
		},

		/**
		 * Update inline menu any time new image selected, or when existing image is replaced
		 * so that new image URL will be replaced
		 * @param {string} value
		 */
		_setCurrentImageKeyAttr: function(value) {
			this._set("currentImageKey", value);
			this.createImageInlineMenu(value);
		},

		_destroyPropertyTooltips: function() {
			this._propertyTooltips.forEach((w) => w.destroy());
			this._propertyTooltips = [];
		},

		createImageInlineMenu: function(/*string*/ imgKey) {
			const imageContainer = this.getImageContainer();
			if (!imageContainer || !this.inlineMenuNode || this.isFixedImageView() || imgKey < 0) {
				return;
			}

			const imageData = this.getCurrentImageData();
			const ctx = this.getContext();
			const {heading} = this.imageSpec;
			let imgMenu;

			html.clear(this.inlineMenuNode, true);

			if (imageData && imgKey) {
				const {editableImage, showWhereUsed} = imageData;
				const attrId = heading.m_id;
				const images = imageContainer[attrId];
				const imageUrl = images[imgKey];
				const actions = [];

				if (showWhereUsed) {
					actions.push(new ImageWhereUsed().init(imageUrl));
				}

				if (!(!ctx.push(heading.m_path) || heading.m_right == xml.RIGHT_NONE || ctx.isReadOnly())) {
					if (editableImage) {
						// determine if Replace Image is valid action
						if (this.getPlugin("image") && showWhereUsed) {
							actions.unshift(new ImageReplace().init(imageUrl, imageContainer.$URL, heading));
						}

						const popupAction = lang.mixin(new Action(), {
							m_id: "ImageEditImage",
							classController: "csi/image/ImageEditorController",
							classControllerParams: {
								imageData,
							},
							m_postAction: (ac) => this.postActionRedrawData(ac),
						});

						popupAction.resolve();
						actions.unshift(popupAction);
					}
				}

				if (actions.length) {
					imgMenu = new InlineActionMenu({
						label: "",
						class: "csi-row-actions-menu",
						showLabel: false,
						bOpenOnHover: false,
						bToggleOpen: true,
						actions,
						materialIcon: "more_horiz",
						onAction: (a) => {
							this._onClickImageAction(a);
						},
					});
					imgMenu.placeAt(this.inlineMenuNode).startup();
				}
			}

			this.inlineImgMenu = imgMenu;
		},

		destroy: function() {
			this._destroyPropertyTooltips();
			this.dndHTML5Destroy();
			this.inherited(arguments);
		},

		/**
		 * @override
		 * Return dropdown options for property custom view
		 */
		getCustomViewDropDownOptions() {
			// Image size dropdown options
			const imageSizes = convertEnumToComponentItem(reflection.IMAGE_SIZE_ENUM);

			// Image label/Cover image
			const imageHeading = this.getImage();
			let imageLabels = [];
			if (imageHeading) {
				const extendedPath = ExtendedPath.create(this.defView.m_rcRoot, imageHeading.m_path, this.defView.pathHintsContext);
				const path = extendedPath.depth && this.specBuilder.pathToSource ? this.specBuilder.pathToSource.clone() : extendedPath;
				const { availableEndClasses } = path;

				for (const clazz of availableEndClasses) {
					const classImageLabels = getCustomViewClassImageLabels(clazz);
					for (const labelObject of classImageLabels) {
						const label = { ...labelObject };

						if (availableEndClasses.length > 1) {
							label.group = locale.type(clazz.type);
						}

						imageLabels.push(label);
					}
				}
			}
			imageLabels.unshift({id: constants.PREFERENCE_DEFAULT, res: "ViewPreferences_DefaultValue"});

			// Property Columns dropdown options
			const propertyColumns = [
				{id: "0", res: "ViewPreferences_DefaultValue"},
				{id: "1", name: "1"},
				{id: "2", name: "2"},
				{id: "3", name: "3"},
				{id: "4", name: "4"}
			];

			// Paper size and paper orientation dropdown options
			const options = this.inherited(arguments);

			return {
				...options,
				imageLabels,
				imageSizes,
				maxPrintImageHeight: IMG_MAX_HEIGHT,
				maxPrintImageWidth: IMG_MAX_WIDTH,
				propertyColumns,
			};
		},
	});
});
